import type { AppCommandItem } from './types'

/**
 * Explicit palette commands for tabbed hub pages whose sub-tabs the route-derived
 * palette can't emit (they live behind dynamic `/:tab` / `:direction?` catch-alls,
 * which routes-to-commands skips). Each href navigates via the existing catch-all,
 * so the page's `useParams(tab)` still resolves the right tab.
 *
 * Gates mirror each destination's real visibility so search never advertises a
 * forbidden page:
 * - UCC / Number Compliance are India-only Phone Numbers tabs.
 * - `isUccOnlyUser` (support / UCC-reviewer sessions) is restricted to the UCC tab
 *   by phone-numbers-combined — every other tab is wrapped in `!isUccOnlyUser` — so
 *   Number Compliance must be withheld from those sessions even if their permission
 *   map includes channel view.
 * - Voice Agents / Usage follow the Analytics report-view (+ usage flag) route gates.
 * - The remaining Phone Numbers tabs (Unrented/Rental Summary/Lookup/Caller IDs/
 *   Port-in/Other Settings/Sender IDs), 10DLC/Toll-free, Alerting (Message/Balance),
 *   Logs (SIP Trunking/Messages/WhatsApp/Verify/Recordings), and SIP Trunking's
 *   Outbound tab mirror the exact per-tab gates their own hub components render
 *   (phone-numbers-combined.tsx, 10dlc/index.tsx, alerting/index.tsx,
 *   sip-trunking.tsx) — see location-effects-handler.tsx for where each boolean
 *   is computed.
 * - `isEnterpriseLocked` AND `isPlanLoaded` together withhold Message Alerts and the
 *   Message log tab on non-Enterprise plans, mirroring the ghosted triggers + voice-tab
 *   redirect their hubs already render. Both are required: the lock reads false until the
 *   profile resolves, so the plan must be *known* unlocked before a command is offered.
 * - The WhatsApp and Verify log tabs follow their own channel locks instead, because that
 *   is what pages/logs/index.tsx and their feature pages gate on. Gating them on the plan
 *   left a Professional account able to open the tabs but unable to find them by search.
 */
export interface TabbedPageCommandGates {
  isIndianRegion: boolean
  isUccOnlyUser: boolean
  hasUccView: boolean
  hasNumbersAccess: boolean
  hasReportView: boolean
  usageReportsEnabled: boolean
  isUsRegion: boolean
  hasSenderIdAccess: boolean
  tendlcEnabled: boolean
  tollfreeEnabled: boolean
  /** `canViewCompliance` (region + `message_channel` view RBAC), resolved by the caller. */
  canViewComplianceTabs: boolean
  canViewMonitor: boolean
  isNonPostpaidAccount: boolean
  canViewLogsTabs: boolean
  canViewBuild: boolean
  /**
   * Enterprise plan lock (`useUserPlan().isEnterpriseLocked`). Gates Message Alerts and
   * the Message log tab, which are ghosted in their own hubs and bounce a locked user
   * back to the voice tab. Voice, SIP Trunking, Recordings and Balance Alerts are
   * unaffected. False while the profile loads, so nothing disappears mid-session for an
   * Enterprise account. WhatsApp and Verify use the channel locks below.
   */
  isEnterpriseLocked: boolean
  /** `useChannelLocks().isWhatsappLocked` — the same gate `/whatsapp` and its log tab use. */
  isWhatsappLocked: boolean
  /** `useChannelLocks().isVerifyLocked` — the same gate `/verify` and its log tab use. */
  isVerifyLocked: boolean
  /** `useChannelLocks().isVerifyAvailable` — false in India, where Verify is not offered. */
  isVerifyAvailable: boolean
  /** `useChannelLocks().isChannelLocksLoaded`; the channel locks fail CLOSED like the plan. */
  isChannelLocksLoaded: boolean
  /**
   * Whether the profile behind the plan has resolved. The palette fails CLOSED here, unlike
   * the page-level gates: `isEnterpriseLocked` is false while loading, so without this a
   * Free/Professional user gets these commands registered on first paint and a quick cmd+K
   * selection reaches the exact locked deep link the gate exists to prevent. Withholding a
   * command for a beat is invisible; offering a dead one is not.
   */
  isPlanLoaded: boolean
  navigateTo: (path: string) => void
}

export const getTabbedPageCommands = ({
  isIndianRegion,
  isUccOnlyUser,
  hasUccView,
  hasNumbersAccess,
  hasReportView,
  usageReportsEnabled,
  isUsRegion,
  hasSenderIdAccess,
  tendlcEnabled,
  tollfreeEnabled,
  canViewComplianceTabs,
  canViewMonitor,
  isNonPostpaidAccount,
  canViewLogsTabs,
  canViewBuild,
  isEnterpriseLocked,
  isWhatsappLocked,
  isVerifyLocked,
  isVerifyAvailable,
  isChannelLocksLoaded,
  isPlanLoaded,
  navigateTo,
}: TabbedPageCommandGates): AppCommandItem[] => {
  const commands: AppCommandItem[] = []
  // Offer a gated tab only once we positively know it is allowed — the palette fails
  // CLOSED, unlike the pages. Messaging is plan-gated; WhatsApp and Verify follow their
  // own channel locks, matching pages/logs/index.tsx.
  const messagingUnlocked = isPlanLoaded && !isEnterpriseLocked
  const whatsappLogsUnlocked = isChannelLocksLoaded && !isWhatsappLocked
  const verifyLogsUnlocked = isVerifyAvailable && isChannelLocksLoaded && !isVerifyLocked

  if (isIndianRegion && hasUccView) {
    commands.push({
      id: 'nav-phone-numbers-ucc',
      label: 'UCC',
      group: 'Deploy',
      hidden: true,
      keywords: ['ucc', 'unsolicited commercial communication', 'dlt', 'phone numbers'],
      action: () => navigateTo('/phone-numbers/ucc'),
    })
  }

  // UCC-only sessions can't reach the compliance tab, so never advertise it to them.
  if (isIndianRegion && !isUccOnlyUser && hasNumbersAccess) {
    commands.push({
      id: 'nav-phone-numbers-compliance',
      label: 'KYC',
      group: 'Deploy',
      hidden: true,
      keywords: ['compliance', 'kyc', 'know your customer', 'regulatory', 'phone numbers'],
      action: () => navigateTo('/phone-numbers/compliance'),
    })
  }

  if (hasReportView) {
    commands.push({
      id: 'nav-analytics-voice-agents',
      label: 'Voice Agents',
      group: 'Analytics',
      hidden: true,
      keywords: ['voice agents', 'analytics', 'reports', 'ai'],
      action: () => navigateTo('/analytics/voice-agents'),
    })
  }

  if (hasReportView && usageReportsEnabled) {
    commands.push(
      {
        id: 'nav-analytics-usage-overview',
        label: 'Usage Overall Spend',
        group: 'Analytics',
        hidden: true,
        keywords: ['usage', 'spend', 'overview', 'analytics', 'reports'],
        action: () => navigateTo('/analytics/usage/overview'),
      },
      {
        id: 'nav-analytics-usage-messaging',
        label: 'Usage Messaging',
        group: 'Analytics',
        hidden: true,
        keywords: ['usage', 'messaging', 'sms', 'spend', 'analytics'],
        action: () => navigateTo('/analytics/usage/messaging'),
      },
      {
        id: 'nav-analytics-usage-voice',
        label: 'Usage Voice',
        group: 'Analytics',
        hidden: true,
        keywords: ['usage', 'voice', 'calls', 'spend', 'analytics'],
        action: () => navigateTo('/analytics/usage/voice'),
      },
    )
  }

  // Remaining Phone Numbers tabs — same defense-in-depth base gate as Number
  // Compliance above (phone-numbers-combined wraps every non-UCC tab in
  // !isUccOnlyUser, and every tab needs channel view via hasNumbersAccess).
  const canAccessPhoneNumbersTabs = !isUccOnlyUser && hasNumbersAccess

  if (canAccessPhoneNumbersTabs) {
    commands.push(
      {
        id: 'nav-phone-numbers-unrented',
        label: 'Unrented Numbers',
        group: 'Deploy',
        hidden: true,
        keywords: ['unrented numbers', 'numbers', 'phone numbers', 'inventory'],
        action: () => navigateTo('/phone-numbers/unrented'),
      },
      {
        id: 'nav-phone-numbers-rental-summary',
        label: 'Rental Summary',
        group: 'Deploy',
        hidden: true,
        keywords: ['rental summary', 'numbers', 'phone numbers', 'billing'],
        action: () => navigateTo('/phone-numbers/rental-summary'),
      },
      {
        id: 'nav-phone-numbers-lookup',
        label: 'Number Lookup',
        group: 'Deploy',
        hidden: true,
        keywords: ['number lookup', 'lookup', 'carrier', 'phone numbers'],
        action: () => navigateTo('/phone-numbers/lookup'),
      },
    )
  }

  // Non-US destinations only — mirrors phone-numbers-combined's `!isIndianRegion` tab gate.
  if (canAccessPhoneNumbersTabs && !isIndianRegion) {
    commands.push({
      id: 'nav-phone-numbers-caller-id',
      label: 'Verified Caller IDs',
      group: 'Deploy',
      hidden: true,
      keywords: ['verified caller ids', 'caller id', 'outbound caller id', 'phone numbers'],
      action: () => navigateTo('/phone-numbers/caller-id'),
    })
  }

  if (canAccessPhoneNumbersTabs && isUsRegion) {
    commands.push(
      {
        id: 'nav-phone-numbers-port-in',
        label: 'Port-in',
        group: 'Deploy',
        hidden: true,
        keywords: ['port-in', 'port in', 'number porting', 'phone numbers'],
        action: () => navigateTo('/phone-numbers/port-in'),
      },
      {
        id: 'nav-phone-numbers-other-settings',
        label: 'Other Settings',
        group: 'Deploy',
        hidden: true,
        keywords: ['other settings', 'phone numbers settings'],
        action: () => navigateTo('/phone-numbers/other-settings'),
      },
    )
  }

  if (canAccessPhoneNumbersTabs && hasSenderIdAccess) {
    commands.push({
      id: 'nav-phone-numbers-sender-id',
      label: 'Sender IDs',
      group: 'Deploy',
      hidden: true,
      keywords: ['sender ids', 'sender id', 'alphanumeric sender', 'phone numbers'],
      action: () => navigateTo('/phone-numbers/sender-id'),
    })
  }

  // 10DLC / Toll-free registration — gated by canViewComplianceTabs (see isComplianceHidden:
  // US-messaging-only + message_channel view RBAC), same as the route itself.
  // Per-type flags mirror CompliancePage's hide10dlc/hideTollFree exactly: only
  // an explicit `false` hides the tab.
  if (canViewComplianceTabs && tendlcEnabled) {
    commands.push({
      id: 'nav-compliance-10dlc',
      label: '10DLC',
      group: 'Deploy',
      hidden: true,
      keywords: ['10dlc', 'campaign registry', 'brand registration', 'compliance'],
      action: () => navigateTo('/compliance/10dlc'),
    })
  }

  if (canViewComplianceTabs && tollfreeEnabled) {
    commands.push({
      id: 'nav-compliance-toll-free',
      label: 'Toll-free',
      group: 'Deploy',
      hidden: true,
      keywords: ['toll-free', 'toll free', 'tfn verification', 'compliance'],
      action: () => navigateTo('/compliance/toll-free'),
    })
  }

  if (canViewMonitor && messagingUnlocked) {
    commands.push({
      id: 'nav-alerting-messages',
      label: 'Message Alerts',
      group: 'Monitor',
      hidden: true,
      keywords: ['message alerts', 'sms alerts', 'delivery alerts', 'alerting'],
      action: () => navigateTo('/alerting/messages'),
    })
  }

  // Postpaid accounts can't have balance alerts — mirrors alerting/index.tsx's
  // synchronous redirect off this tab for postpaid accounts.
  if (canViewMonitor && isNonPostpaidAccount) {
    commands.push({
      id: 'nav-alerting-balance-alerts',
      label: 'Balance Alerts',
      group: 'Monitor',
      hidden: true,
      keywords: ['balance alerts', 'low balance', 'threshold alerts', 'alerting'],
      action: () => navigateTo('/alerting/balance-alerts'),
    })
  }

  if (canViewLogsTabs) {
    commands.push(
      {
        id: 'nav-logs-sip-trunking',
        label: 'SIP Trunking Logs',
        group: 'Monitor',
        hidden: true,
        keywords: ['sip trunking logs', 'zentrunk', 'cdr', 'logs'],
        action: () => navigateTo('/logs/sip-trunking'),
      },
      {
        id: 'nav-logs-recordings',
        label: 'Recordings',
        group: 'Monitor',
        hidden: true,
        keywords: ['recordings', 'call recording', 'logs', 'voice'],
        action: () => navigateTo('/logs/recordings'),
      },
    )

    // Gated log tabs: ghosted in pages/logs/index.tsx and redirected to /logs/voice when
    // locked, so offering one here would be a dead end. Each follows the same signal its
    // tab does — messaging the plan, WhatsApp and Verify their channel locks.
    if (messagingUnlocked) {
      commands.push({
        id: 'nav-logs-messages',
        label: 'Message Logs',
        group: 'Monitor',
        hidden: true,
        keywords: ['message logs', 'sms logs', 'logs'],
        action: () => navigateTo('/logs/messages'),
      })
    }

    if (whatsappLogsUnlocked) {
      commands.push({
        id: 'nav-logs-whatsapp',
        label: 'WhatsApp Logs',
        group: 'Monitor',
        hidden: true,
        keywords: ['whatsapp logs', 'logs'],
        action: () => navigateTo('/logs/whatsapp'),
      })
    }

    if (verifyLogsUnlocked) {
      commands.push({
        id: 'nav-logs-verify',
        label: 'Verify Logs',
        group: 'Monitor',
        hidden: true,
        keywords: ['verify logs', 'otp logs', 'logs'],
        action: () => navigateTo('/logs/verify'),
      })
    }
  }

  if (canViewBuild) {
    commands.push({
      id: 'nav-sip-outbound',
      label: 'Outbound Trunks',
      group: 'Build',
      hidden: true,
      keywords: ['outbound trunks', 'sip trunking', 'trunks'],
      action: () => navigateTo('/sip-trunking/outbound'),
    })
  }

  return commands
}
